import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { GraduationCap, Plus, Pencil, Trash2, Users, ClipboardCheck, Award, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = ['registered', 'onboarding', 'training', 'assessment', 'moderation', 'certified', 'placed', 'employed', 'exited'];
const STAGE_CLS = {
  registered: 'bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400',
  onboarding: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400',
  training: 'bg-violet-100 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400',
  assessment: 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400',
  moderation: 'bg-cyan-100 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400',
  certified: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  placed: 'bg-teal-100 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400',
  employed: 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400',
  exited: 'bg-rose-100 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400',
};
const EMPTY = { name: '', email: '', programme: '', host_employer: '', stage: 'registered', attendance_pct: '', assessment_result: 'pending', poe_complete: false, certified_date: '', placement_employer: '', placement_date: '', employment_outcome: '', stipend_status: 'n/a', notes: '' };

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <Card className="!p-4" data-testid={testid}>
    <CardContent className="p-0 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
      <div><p className="text-xl font-bold font-mono leading-tight">{value ?? 'No data'}</p><p className="text-xs text-muted-foreground">{label}</p></div>
    </CardContent>
  </Card>
);

export default function LearnersPage() {
  const [learners, setLearners] = useState([]);
  const [dash, setDash] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      const [l, d] = await Promise.all([axios.get(`${API}/learners`), axios.get(`${API}/learners/dashboard/summary`)]);
      setLearners(l.data); setDash(d.data);
    } catch (e) { toast.error('Failed to load learners'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    const payload = { ...form,
      attendance_pct: form.attendance_pct === '' ? null : parseFloat(form.attendance_pct),
      certified_date: form.certified_date || null, placement_date: form.placement_date || null };
    try {
      if (editing) await axios.put(`${API}/learners/${editing.id}`, payload);
      else await axios.post(`${API}/learners`, payload);
      toast.success(editing ? 'Learner updated' : 'Learner registered');
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete learner "${l.name}"?`)) return;
    try { await axios.delete(`${API}/learners/${l.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const filtered = stageFilter === 'all' ? learners : learners.filter(l => l.stage === stageFilter);

  return (
    <div className="space-y-6" data-testid="learners-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="learners-title">
            <GraduationCap className="h-7 w-7 text-primary" /> Learner Lifecycle
          </h1>
          <p className="text-muted-foreground mt-1">Registration → Training → Assessment → Certification → Placement</p>
        </div>
        <Button className="rounded-xl gap-2" onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }} data-testid="new-learner-btn">
          <Plus className="h-4 w-4" /> Register Learner
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat icon={Users} label="Active Learners" value={dash?.active} tone="bg-blue-100 dark:bg-blue-500/10 text-blue-600" testid="learner-active" />
        <Stat icon={ClipboardCheck} label="Attendance Avg" value={dash?.attendance_avg != null ? `${dash.attendance_avg}%` : null} tone="bg-violet-100 dark:bg-violet-500/10 text-violet-600" testid="learner-attendance" />
        <Stat icon={Award} label="Pass Rate" value={dash?.pass_rate != null ? `${dash.pass_rate}%` : null} tone="bg-amber-100 dark:bg-amber-500/10 text-amber-600" testid="learner-passrate" />
        <Stat icon={GraduationCap} label="Certified" value={dash?.certification_rate != null ? `${dash.certification_rate}%` : null} tone="bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600" testid="learner-certified" />
        <Stat icon={Briefcase} label="Placed" value={dash?.placement_rate != null ? `${dash.placement_rate}%` : null} tone="bg-teal-100 dark:bg-teal-500/10 text-teal-600" testid="learner-placed" />
      </div>

      {/* Stage funnel chips */}
      <div className="flex gap-2 flex-wrap" data-testid="learner-stage-chips">
        <button onClick={() => setStageFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${stageFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
          All ({learners.length})
        </button>
        {STAGES.map(s => (
          <button key={s} onClick={() => setStageFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize border ${stageFilter === s ? 'ring-2 ring-primary/50' : ''} ${STAGE_CLS[s]}`}
            data-testid={`learner-stage-${s}`}>
            {s} ({dash?.by_stage?.[s] ?? 0})
          </button>
        ))}
      </div>

      <Card className="!p-0">
        <CardContent className="p-0 divide-y">
          {filtered.length ? filtered.map(l => (
            <div key={l.id} className="flex items-center gap-3 p-4 hover:bg-muted/40" data-testid={`learner-row-${l.id}`}>
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                {(l.name || '?').charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{l.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {l.programme || 'No programme'}
                  {l.host_employer ? ` · ${l.host_employer}` : ''}
                  {l.attendance_pct != null ? ` · ${l.attendance_pct}% attendance` : ''}
                </p>
              </div>
              {l.assessment_result === 'pass' && <Badge className="bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 shrink-0" variant="secondary">Pass</Badge>}
              {l.assessment_result === 'fail' && <Badge className="bg-rose-100 dark:bg-rose-500/10 text-rose-600 shrink-0" variant="secondary">Fail</Badge>}
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize shrink-0 ${STAGE_CLS[l.stage]}`}>{l.stage}</span>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { setEditing(l); setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(l).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v ?? (k === 'poe_complete' ? false : '')])) }); setShowForm(true); }} data-testid={`learner-edit-${l.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => remove(l)} data-testid={`learner-delete-${l.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )) : (
            <div className="p-10 text-center text-muted-foreground" data-testid="learners-empty">
              <GraduationCap className="h-10 w-10 mx-auto mb-2 opacity-40" />
              No learners in this view — register your first learner.
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="learner-form-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Learner' : 'Register Learner'}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="learner-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Programme</Label>
              <Input value={form.programme} onChange={e => setForm({ ...form, programme: e.target.value })} data-testid="learner-programme-input" />
            </div>
            <div>
              <Label>Host Employer</Label>
              <Input value={form.host_employer} onChange={e => setForm({ ...form, host_employer: e.target.value })} />
            </div>
            <div>
              <Label>Lifecycle Stage</Label>
              <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="learner-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Attendance (%)</Label>
              <Input type="number" min="0" max="100" value={form.attendance_pct} onChange={e => setForm({ ...form, attendance_pct: e.target.value })} data-testid="learner-attendance-input" />
            </div>
            <div>
              <Label>Assessment Result</Label>
              <Select value={form.assessment_result} onValueChange={v => setForm({ ...form, assessment_result: v })}>
                <SelectTrigger data-testid="learner-assessment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="pass">Pass</SelectItem>
                  <SelectItem value="fail">Fail</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stipend Status</Label>
              <Select value={form.stipend_status} onValueChange={v => setForm({ ...form, stipend_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="n/a">N/A</SelectItem>
                  <SelectItem value="current">Current</SelectItem>
                  <SelectItem value="arrears">Arrears</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Certified Date</Label>
              <Input type="date" value={form.certified_date} onChange={e => setForm({ ...form, certified_date: e.target.value })} />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" id="poe" checked={form.poe_complete} onChange={e => setForm({ ...form, poe_complete: e.target.checked })} className="h-4 w-4 accent-blue-600" />
              <Label htmlFor="poe">PoE Complete</Label>
            </div>
            <div>
              <Label>Placement Employer</Label>
              <Input value={form.placement_employer} onChange={e => setForm({ ...form, placement_employer: e.target.value })} />
            </div>
            <div>
              <Label>Placement Date</Label>
              <Input type="date" value={form.placement_date} onChange={e => setForm({ ...form, placement_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Employment Outcome / Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} data-testid="learner-save-btn">{editing ? 'Update' : 'Register'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
